import React, { useEffect, useState, useRef, useContext } from "react";
import { createPortal } from "react-dom";

import GenderForm from "./components/GenderForm";
import BirthdayForm from "./components/BirthdayForm";
import EmailForm from "./components/EmailForm";
import NameForm from "./components/NameForm";
import PasswordForm from "./components/PasswordForm";
import PromoStep from "./components/PromoStep";
import QuestionRadio from "./components/QuestionRadio";
import { Footer } from "./components/Footer";
import PopUp from "./components/PopUp";
import QuestionCheckbox from "./components/QuestionCheckbox";
import AgeMatch from "./components/AgeMatch";

import { simplyClick } from "./helpers/index";
import { getTTI, sendScreenResolution, getCookie } from "./helpers";
import {
  EVENTS_SIGN_IN,
  landingId,
  optionsQ1,
  optionsQ2,
  optionsQ3,
  optionsQ4,
  optionsQ5,
} from "./constants";
import { ContextProfileUser } from "./lib/profileContext";
import { useAppendLandingId } from "./hooks/useAppendLandingId";
import { useSessionData } from "ui-components";

function App() {
  const [step, setStep] = useState(() => {
    const nav = performance.getEntriesByType("navigation")[0];
    const isReload = nav ? nav.type === "reload" : false;
    return isReload ? 1 : window.history.state?.step || 1;
  });

  const [showPopUp, setShowPopUp] = useState(false);
  const footer = document.getElementById("footer");
  let content = null;

  const speedRef = useRef(0.5);

  const { data, setData } = useContext(ContextProfileUser);
  const { getSessionData, saveSessionData } = useSessionData(landingId);

  useEffect(() => {
    const devStep = getCookie("DEV_MODE_STEP");
    if (devStep !== false) {
      const stepNum = Number(devStep);
      if (!isNaN(stepNum) && stepNum >= 1 && stepNum <= 73) {
        setStep(stepNum);
      }
    }

    const handlePopState = (event) => {
      const newStep = event.state?.step;
      if (newStep) setStep(newStep);
    };

    const handlePageShow = (event) => {
      if (event.persisted) {
        const newStep = window.history.state?.step;
        if (newStep) setStep(newStep);
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    const currentState = window.history.state;
    if (!currentState || currentState.step !== step) {
      window.history.replaceState({ ...currentState, step }, "");
    }
  }, [step]);

  useEffect(() => {
    const loader = document.getElementById("loader");
    if (loader) {
      loader.style.display = "none";
    }

    const handleFocus = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        document.body.classList.add("keyboard-open");
      }
    };

    const handleBlur = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        setTimeout(() => {
          document.body.classList.remove("keyboard-open");
        }, 200);
      }
    };

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleBlur);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleBlur);
    };
  }, []);

  const scrollToTop = (behavior = "auto") => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const supportsSR = "scrollRestoration" in window.history;
    const prevSR = supportsSR ? window.history.scrollRestoration : undefined;

    if (supportsSR) {
      window.history.scrollRestoration = "manual";
    }

    scrollToTop("auto");
    requestAnimationFrame(() => scrollToTop("auto"));
    setTimeout(() => scrollToTop("auto"), 0);

    const onLoad = () => scrollToTop("auto");
    window.addEventListener("load", onLoad);

    return () => {
      window.removeEventListener("load", onLoad);
      if (supportsSR) {
        window.history.scrollRestoration = prevSR ?? "auto";
      }
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    const signInButton = document.querySelector(".sign-in");

    const handleClick = () => {
      const eventName = EVENTS_SIGN_IN[step - 1];
      if (eventName) {
        simplyClick(eventName);
      }
    };

    if (signInButton) {
      signInButton.addEventListener("click", handleClick);
    }

    return () => {
      if (signInButton) {
        signInButton.removeEventListener("click", handleClick);
      }
    };
  }, [step]);

  useAppendLandingId(landingId);

  const openPopUp = (e) => {
    if (e.target === e.currentTarget) {
      setShowPopUp(true);
      simplyClick("cl_lnd_photos", { text: `st${step}` });
    }
  };

  const closePopUp = () => {
    setShowPopUp(false);
  };

  useEffect(() => {
    let popupRoot = document.getElementById("popup-root");
    if (!popupRoot) {
      popupRoot = document.createElement("div");
      popupRoot.id = "popup-root";
      document.body.appendChild(popupRoot);
    }

    if (window.location.search !== "") {
      const signInButton = document.querySelector(".sign-in");
      if (signInButton) {
        signInButton.setAttribute("href", `/login/${window.location.search}`);
      }
    }

    const timer = setTimeout(() => {
      getTTI();
      sendScreenResolution();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const registerGoogleContainer = document.querySelector(".content__wrapper");

    if (registerGoogleContainer && !showPopUp) {
      registerGoogleContainer.addEventListener("click", openPopUp);
    }

    return () => {
      if (registerGoogleContainer) {
        registerGoogleContainer.removeEventListener("click", openPopUp);
      }
    };
  }, [step, showPopUp]);

  const getBaseSpeed = () => {
    return window.innerWidth < 480 ? 0.3 : 0.5;
  };

  useEffect(() => {
    speedRef.current = showPopUp ? 0.3 : getBaseSpeed();
  }, [showPopUp]);

  useEffect(() => {
    const wrapper = document.querySelector(".content__wrapper");
    if (!wrapper) return;
    let position = 0;
    let lastTimestamp = performance.now();
    let animationFrameId;
    const animateBg = (timestamp) => {
      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      position += speedRef.current * (elapsed / 16);
      wrapper.style.backgroundPosition = `${position}px 56px`;
      animationFrameId = requestAnimationFrame(animateBg);
    };
    animationFrameId = requestAnimationFrame(animateBg);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  useEffect(() => {
    const saved = getSessionData();
    if (saved) {
      setData(saved);
    }
  }, []);
  
  useEffect(() => {
    if (Object.keys(data).length > 0) {
      saveSessionData(data);
    }
  }, [data, saveSessionData]);

  switch (step) {
    case 2:
      content = <GenderForm nextStep={() => setStep(3)} />;
      break;
    case 3:
      content = <NameForm nextStep={() => setStep(4)} />;
      break;
    case 4:
      content = <BirthdayForm nextStep={() => setStep(5)} />;
      break;
    case 5:
      content = (
        <QuestionRadio
          step={step}
          eventOption={"cl_lnd_marital_status_st5_status"}
          eventAction={"cl_lnd_marital_status_st5_action"}
          activeDot={5}
          titleHeader={"About me"}
          title={"How’s your love life?"}
          options={optionsQ1}
          nextStep={() => setStep(6)}
        />
      );
      break;
    case 6:
      content = (
        <QuestionRadio
          step={step}
          eventOption={"cl_lnd_physique_st6_physique"}
          eventAction={"cl_lnd_physique_st6_action"}
          activeDot={6}
          titleHeader={"About me"}
          title={"Which describes your looks best?"}
          options={optionsQ2}
          nextStep={() => setStep(7)}
        />
      );

      break;
    case 7:
      content = (
        <QuestionCheckbox
          eventOption={"cl_lnd_topics_st7_topics"}
          eventAction={"cl_lnd_topics_st7_action"}
          activeDot={7}
          step={step}
          title={"Pick 3 to 5 interests and hobbies"}
          options={optionsQ3}
          nextStep={() => setStep(8)}
        />
      );
      break;
    case 8:
      content = (
        <QuestionRadio
          step={step}
          eventOption={"cl_lnd_looking_st8_looking"}
          eventAction={"cl_lnd_looking_st8_action"}
          activeDot={8}
          titleHeader={"About my match"}
          title={"What are you looking for?"}
          options={optionsQ4}
          nextStep={() => setStep(9)}
        />
      );
      break;
    case 9:
      content = (
        <AgeMatch step={step} activeDot={9} nextStep={() => setStep(10)} />
      );
      break;
    case 10:
      content = (
        <QuestionRadio
          step={step}
          eventOption={"cl_lnd_body_type_st10_body"}
          eventAction={"cl_lnd_body_type_st10_action"}
          activeDot={10}
          titleHeader={"About my match"}
          title={"What’s your best match look like?"}
          options={optionsQ5}
          nextStep={() => setStep(11)}
        />
      );
      break;
    case 11:
      content = <EmailForm nextStep={() => setStep(12)} />;
      break;
    case 12:
      content = <PasswordForm nextStep={() => setStep(13)} />;
      break;
    default:
      content = <PromoStep nextStep={() => setStep(2)} />;
      break;
  }

  return (
    <>
      {showPopUp &&
        createPortal(
          <PopUp step={step} closePopUp={closePopUp} />,
          document.getElementById("popup-root")
        )}
      {content}
      {footer && createPortal(<Footer step={step} />, footer)}
    </>
  );
}

export default App;
